import { useEffect, useRef, useState } from 'react';

import ReCAPTCHA from 'react-google-recaptcha';

import { useFormik } from 'formik';
import { SITE_KEY } from 'views/SelfServe/SignUp/utils/constants';
import * as yup from 'yup';

import Button from 'components/Button/Button';
import Input from 'components/Input/Input';
import { EModalState } from 'components/LoginModal/LoginModal.types';

import { required } from '../../utils/errorsTextHelper';
import { EToasterStatus, showToast } from '../../utils/showToast';
import Typography from '../Typography/Typography';

import { PasswordResetFormProps } from './ForgotPasswordForm.types';

const ForgotPasswordForm = ({
  sendResetEmail,
  setModalState
}: PasswordResetFormProps) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  let formSchema = yup.object().shape({
    email: yup.string().required(required('Email')).email('Enter a valid email')
  });
  const {
    values,
    errors,
    touched,
    dirty,
    handleChange,
    handleBlur,
    submitForm,
    isValid
  } = useFormik({
    initialValues: {
      email: ''
    },
    validationSchema: formSchema,
    onSubmit: async (values) => {
      values.email = values.email.toLowerCase();

      const recaptchaToken = await recaptchaRef.current?.executeAsync();
      if (!recaptchaToken) {
        showToast({
          message: 'Please complete the reCAPTCHA verification',
          status: EToasterStatus.ERROR
        });
        return;
      }
      recaptchaRef.current?.reset();

      setIsLoading(true);
      sendResetEmail(values.email, recaptchaToken, () => {
        setIsLoading(false);
      });
    }
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    localStorage.clear();
  }, [values]);

  return (
    <div className="w-full">
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={SITE_KEY ?? 'sitekey'}
        size="invisible"
        className="hidden"
      />
      <div className="flex flex-col gap-y-2 text-center mb-6">
        <Typography tag="h4">Forgot Password</Typography>
        <Typography className="text-p-s text-base-mutedForeground">
          Enter your email and we'll send you instructions on how to reset your
          password.
        </Typography>
      </div>
      <form
        onSubmit={(e: React.SyntheticEvent) => {
          e.preventDefault();
        }}
        className="flex flex-col gap-y-4"
      >
        <Input
          label="Email"
          placeholder="Email"
          name="email"
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          state={errors.email && touched.email ? 'error' : 'default'}
          description={errors.email && touched.email ? errors.email : ''}
        />
        <Button
          className="w-full"
          disabled={!!Object.keys(errors).length || !isValid || !dirty}
          onClick={submitForm}
          isLoading={isLoading}
        >
          Send
        </Button>
      </form>
      <Typography
        className="text-p-s cursor-pointer hover:opacity-80 underline text-center mt-9"
        onClick={() => setModalState(EModalState.LOGIN)}
      >
        Go back to login page
      </Typography>
    </div>
  );
};

export default ForgotPasswordForm;
